import React, { useState, useEffect } from 'react';
import { Helmet } from "react-helmet";
import { Link } from '../components/Router';
import { isFollowed, updateFollowed } from '../components/Profile';
import { sortBy } from "../components/Sorting";
import { callLink } from '../components/Links';
import { dayAndTime } from '../components/Dates';
import { recordEvent } from '../components/Analytics';
import { useSessionContext } from '../components/Session';

import Button from 'react-bootstrap/Button';
import companyLogo from '../img/zyro.png';
import playWhiteButton from "../img/play-white-button.svg";

import log from 'loglevel';
import { isAfter } from 'date-fns';
import {
  FaCalendarCheck,
  FaClock
} from 'react-icons/fa';
import {
  BiBlock
} from 'react-icons/bi';
import SiteHead from './SiteHead';
import Layout from './Layout';
import HeroBgImage from "../img/hero-bg.png";

import '../sass/call-log.scss';

const EarningsEvent = ({ exchange, symbol, isApproved, optOut, event: { year, quarter, conference_date, audio_file } }) => {
  const conferenceDate = new Date(conference_date);
  const today = new Date();
  const conferenceInFuture = isAfter(conferenceDate, today);
  const callPath = callLink(exchange, symbol, year, quarter);
  const { day, time } = dayAndTime(new Date(conference_date));
  log.debug("EarningsEvent::render()", year, quarter.toUpperCase(), conference_date, isApproved, optOut);

  return (
    <>
      <tr>
        <td>
          <span>
            {/*<Badge variant="dark">*/}
              { quarter ? quarter.toUpperCase() : "Q?" }
            {/*</Badge>*/}
          </span>
        </td>
        <td>{year}</td>
        <td>{day}</td>
        <td>{time}</td>
        <td>
          <Link className="btn sound-lnk btn-success" to={callPath}>
            <span className="icon">
              {
                optOut ?
                <>
                  <BiBlock />
                </>
                :
                <>
                  { audio_file ?
                    <>
                      { isApproved ?
                        <img
                          src={playWhiteButton}
                          alt="icon"
                        />
                        :
                        <FaClock />
                      }
                    </>
                    :
                    <>
                      { conferenceInFuture ? <FaCalendarCheck /> : <FaClock /> }
                    </>
                  }
                </>
              }
            </span>
            { optOut?
              <>
                Restricted
              </>
              :
              <>
                { audio_file ?
                  <>
                    { isApproved ? "Listen" : "Awaiting Approval" }
                  </>
                  :
                  <>
                    { conferenceInFuture ? "Scheduled" : "Pending" }
                  </>
                }
              </>
            }
          </Link>
        </td>
      </tr>
    </>
  );
};

const CompanyFollowThingy = ({ exchange, symbol, buildVersion, followed, setFollowed }) => {
  const { sessionContext } = useSessionContext();

  useEffect(() => {
    setFollowed(isFollowed(exchange, symbol));
  }, [exchange, symbol]);

  const onFollowClick = () => {
    log.info("Followed!", exchange, symbol);
    updateFollowed(exchange, symbol, !followed, sessionContext);
    setFollowed(!followed);
    recordEvent({ name: followed ? "UnFollow" : "Follow", exchange, symbol, buildVersion });
  };

  return (
    <Button variant="primary" onClick={onFollowClick}>
      { followed ? "Unfollow" : "Follow" }
    </Button>
  );
};

const getFollowerCount = (analytics) => {
  log.debug("Analytics", analytics);
  const { Play = 0, Pause = 0, Company = 0, Follow = 0, UnFollow = 0, SeekForward = 0 } = analytics;
  return Follow - UnFollow;
};

const computeFollowerCountIncludingSelf = (followedCount, followed) => {
  if (followed) {
    return followedCount + 1;
  }
  return followedCount;
}

const CompanyCalls = (props) => {

  const {
    pageContext: {
      exchange, symbol, earnings, analytics = {}, securityName, buildVersion, isApproved, optOut
    }
  } = props;

  const [ followed, setFollowed ] = useState(false);
  const followerCount = getFollowerCount(analytics);
  const followerCountIncludingSelf = computeFollowerCountIncludingSelf(followerCount, followed);

  log.trace("Company::Render()", exchange, symbol, analytics);

  useEffect(() => {
    recordEvent({ name: "Company", exchange, symbol, buildVersion });
  }, [exchange, symbol, buildVersion]);

  let earningsEvents = {};
  earnings.forEach((event) => {
    log.trace("Company:", event);
    const { year = "20??" } = event;
    if (!(year in earningsEvents)) {
      earningsEvents[year] = [];
    }
    if (!("quarter" in event)) {
      event.quarter = "Q?";
    }
    earningsEvents[year].push(event);
  });

  const orderedEvents = [];
  Object.keys(earningsEvents).sort().reverse().forEach(year =>
    {
      earningsEvents[year].sort(sortBy("quarter")).reverse().forEach(event =>
        orderedEvents.push(
          <EarningsEvent
            key={`${year}-${event.quarter}`}
            exchange={exchange}
            symbol={symbol} 
            event={event}
            isApproved={isApproved}
            optOut={optOut}
          />
        )
      )
    }
  );
  return (
    <>
      <SiteHead
        title={`EarningsCall · ${securityName}`}
        description={`Check out all the earnings calls for ${securityName}`}
        pageProps={props}
      />
      <Helmet>
        {/* Ensure the HeroBgImage is preloaded */}
        <link rel="preload" as="image" href={HeroBgImage} />
      </Helmet>
      <section className="common-hero-section">
        <div className="container">
          <div className="hero-card-content"></div>
        </div>
      </section>

      <section className="call-log-section">
        <div className="container">
          <div className="profile-card">
            <div className="img-container invisible">
              <img src={companyLogo} alt="Company Logo" />
            </div>
            <div className="info-container">
              <div className="title">
                <h1>
                  {securityName}
                </h1>
              </div>
              <div className="info">
                <div className="call-info">
                  <span className="text-primary">{earnings.length}</span>&nbsp;Call{earnings.length !== 1 && "s"}
                </div>
                <div className="invisible">
                  <p>Build Version: {buildVersion}</p>
                </div>
                <div className="follower-info">
                  <span className="text-primary">{ followerCountIncludingSelf }</span>&nbsp;Follower{followerCountIncludingSelf !== 1 && "s"}
                </div>
                {/* <div className="follower-info">
                  <span className="text-primary">199</span>&nbsp;Listens
                </div> */}
              </div>
            </div>
            <div className="button-container">
              <CompanyFollowThingy
                exchange={exchange}
                symbol={symbol}
                buildVersion={buildVersion}
                followed={followed}
                setFollowed={setFollowed}
              />
            </div>
          </div>

          <ul className="nav nav-pills invisible" id="pills-tab" role="tablist">
            <li className="nav-item" role="presentation">
              <a
                className="nav-link active"
                id="pills-recentCalls-tab"
                data-toggle="pill"
                href="#pills-recentCalls"
                role="tab"
                aria-controls="pills-recentCalls"
                aria-selected="true"
                >Recent Calls
              </a>
            </li>
            <li className="nav-item" role="presentation">
              <a
                className="nav-link"
                id="pills-upcomingCalls-tab"
                data-toggle="pill"
                href="#pills-upcomingCalls"
                role="tab"
                aria-controls="pills-upcomingCalls"
                aria-selected="false"
                >Upcoming Call
              </a>
            </li>
          </ul>

          <div className="tab-content" id="pills-tabContent">
            <div
              className="tab-pane fade show active"
              id="pills-recentCalls"
              role="tabpanel"
              aria-labelledby="pills-recentCalls-tab"
            >
              <div className="table-container">
                <div className="table-responsive">
                  <table>
                    <tbody>
                      { orderedEvents }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

const CompanyCallsExport = (props) => {
  const { pageContext } = props;
  const { exchange, symbol } = pageContext;
  const layoutProps = {
    ...props,
    analyticsOptions: {
      exchange,
      symbol,
    }
  };
  return (
    <Layout {...layoutProps}>
      <CompanyCalls {...props} />
    </Layout>
  );
};

export default CompanyCallsExport;
